/*
 * Booking-status palette — the single source of truth.
 *
 * These colours used to be written out twice: once in the if/else chain that
 * builds timeline items, and again, hardcoded, in the legend markup. They had
 * already drifted apart. Both now read from here.
 *
 * Each status renders the way the design system's `tag.light` variant does,
 * which is also what the Today view's handover spec asks for ("status as a
 * left stripe and tint") and what the Day/Week mockups show:
 *
 *   tint   — pale block background      (tag.light.<variant>.bg)
 *   text   — dark, readable, on `tint`  (tag.light.<variant>.text)
 *   stripe — saturated 3px left bar, and the legend dot
 *            (tag.outlined.<variant>.border)
 *   solid / textOnSolid — the selected state (tag.solid.<variant>)
 *
 * The previous treatment — saturated fill, white 9px text, no stripe — is what
 * made the grid read as heavy next to the Today view.
 *
 * ── On design-system alignment ──────────────────────────────────────────────
 * Matrix renders eight simultaneous booking statuses. The design system's
 * feedback palette has four semantic slots (info / success / warning / error),
 * so not every status can map onto it. Entries below are marked:
 *
 *   [DS]  exactly the design system's feedback ramp
 *   [--]  no design-system equivalent; the existing matrix hue is kept and
 *         only restyled to the tag.light shape (pale tint + dark text)
 *
 * The [--] entries are a real gap: a booking-status ramp is something the
 * design system does not yet have, and picking one is a design decision rather
 * than something to settle here. `cancelled` is a happy accident — matrix's
 * existing #FFD740 is already exactly Yellow 300 / feedback.warning.bg-strong.
 */

const STATUS_STYLES = {
  // [--] no design-system equivalent. DS feedback.error is the nearest hue,
  // but an unconfirmed booking is not semantically an error, so the existing
  // matrix orange-red is kept rather than forced onto the error ramp.
  pending: {
    tint: "#ffeae6",
    text: "#993119",
    stripe: "#ff512b",
    solid: "#ff512b",
    textOnSolid: "#ffffff",
  },

  // [DS] feedback.success
  confirmed: {
    tint: "#deecd9",
    text: "#375f28",
    stripe: "#5c9f43",
    solid: "#5c9f43",
    textOnSolid: "#ffffff",
  },

  // [--] no design-system equivalent. Sits deliberately close to `confirmed`
  // because it always did; both are green and the distinction is the shade.
  precheckin: {
    tint: "#edfbf4",
    text: "#2f6548",
    stripe: "#6ae0a1",
    solid: "#6ae0a1",
    textOnSolid: "#000000",
  },

  // [DS] feedback.info
  checkin: {
    tint: "#d2e4fe",
    text: "#134796",
    stripe: "#1f77fa",
    solid: "#1f77fa",
    textOnSolid: "#ffffff",
  },

  // [DS] neutral ramp — a completed stay recedes rather than competing for
  // attention, which the old solid black very much did not.
  checkout: {
    tint: "#f2f3f4",
    text: "#464646",
    stripe: "#575757",
    solid: "#575757",
    textOnSolid: "#ffffff",
  },

  // [--] no design-system equivalent.
  renewed: {
    tint: "#fff2f0",
    text: "#995a4d",
    stripe: "#ff9680",
    solid: "#ff9680",
    textOnSolid: "#000000",
  },

  // [DS] feedback.error — blocked time genuinely is "unavailable".
  timeblock: {
    tint: "#ffdbdc",
    text: "#992e2f",
    stripe: "#992e2f",
    solid: "#992e2f",
    textOnSolid: "#ffffff",
  },

  // [DS] feedback.error, strong — needs to read as louder than `timeblock`.
  unassigned: {
    tint: "#ffdbdc",
    text: "#992e2f",
    stripe: "#ff4d4f",
    solid: "#ff4d4f",
    textOnSolid: "#ffffff",
  },

  // [DS] feedback.warning. matrix's existing #ffd740 is already Yellow 300.
  cancelled: {
    tint: "#fff7d9",
    text: "#332b0d",
    stripe: "#ffd740",
    solid: "#ffd740",
    textOnSolid: "#332b0d",
  },

  // [DS] royal-blue ramp, one step softer than `checkin` so a cost sitting
  // next to a booking is distinguishable from it.
  expenses: {
    tint: "#d2e4fe",
    text: "#134796",
    stripe: "#79adfc",
    solid: "#79adfc",
    textOnSolid: "#134796",
  },

  // [DS] neutral — the fallback for an unrecognised purpose.
  others: {
    tint: "#f2f3f4",
    text: "#464646",
    stripe: "#c8c8c8",
    solid: "#c8c8c8",
    textOnSolid: "#000000",
  },
};

/*
 * Legend sections and their rows, in display order: booking statuses first,
 * then the non-booking blocks. Each section's `key` is also its i18n key.
 *
 * `expenses` is filtered out by the caller when the node has no expenses.
 *
 * NOTE: the row that reads "renewed" here used to call filterOrder("renew")
 * while items were tagged "renewed", so the toggle matched nothing — renewed
 * bookings could never be filtered out and the legend dot was permanently
 * dimmed. These keys are now the item status values themselves, so they cannot
 * drift apart again.
 */
const LEGEND_SECTIONS = [
  {
    key: "statuses",
    statuses: [
      "pending",
      "confirmed",
      "precheckin",
      "checkin",
      "checkout",
      "renewed",
      "cancelled",
    ],
  },
  {
    key: "others",
    statuses: ["timeblock", "expenses"],
  },
];

const getStatusStyle = (status) =>
  STATUS_STYLES[status] || STATUS_STYLES.others;

export { STATUS_STYLES, LEGEND_SECTIONS, getStatusStyle };
